// #tag: USER AND APPEARANCE SETTINGS SCREEN
// #purpose: Configuration center for themes (light, dark, system), accent palettes, typography sizing, audio cues, and multi-channel notification digests.
import React from 'react';
import { Link } from 'react-router-dom';
import { Illustration } from '../components/common/Illustration';
import { SectionHeader } from '../components/common/Cards';
import { useAppearance } from '../context/AppearanceProvider';
import { useAuth } from '../context/AuthProvider';
import { useUiSound } from '../hooks/useUiSound';
import {
  useNotificationConfig,
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '../hooks/useExamData';
import type { NotificationPreferences } from '../types';

const CHANNEL_LABELS: Record<string, string> = {
  in_app: 'In-app',
  push: 'Push',
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};

const DIGEST_OPTIONS: NotificationPreferences['digestMode'][] = ['instant', 'daily', 'weekly'];

function NotificationPreferencesPanel() {
  const { isAuthenticated } = useAuth();
  const { data: configData } = useNotificationConfig();
  const { data: prefsData, isLoading } = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();

  if (!isAuthenticated) {
    return (
      <article className="glass-surface wfx-settings-card wfx-settings-card--wide">
        <h2>Notification preferences</h2>
        <p className="wfx-muted">Sign in to customize delivery channels, categories, and digest settings.</p>
      </article>
    );
  }

  if (isLoading || !prefsData) {
    return (
      <article className="glass-surface wfx-settings-card wfx-settings-card--wide">
        <h2>Notification preferences</h2>
        <p className="wfx-muted">Loading preferences…</p>
      </article>
    );
  }

  const prefs = prefsData.preferences;

  const patch = (partial: Partial<NotificationPreferences>) => {
    updatePrefs.mutate(partial);
  };

  return (
    <article className="glass-surface wfx-settings-card wfx-settings-card--wide">
      <h2>Notification preferences</h2>
      <p className="wfx-muted">
        Event-driven alerts for exams, boards, and universities you follow. Platform news is off by default.
      </p>

      <label className="wfx-toggle-row">
        <span>Only exams I follow</span>
        <input
          type="checkbox"
          checked={prefs.personalizedOnly}
          aria-label="Only notify for followed exams"
          onChange={(e) => patch({ personalizedOnly: e.target.checked })}
        />
      </label>

      <label className="wfx-toggle-row">
        <span>Platform announcements</span>
        <input
          type="checkbox"
          checked={prefs.platformAnnouncements}
          aria-label="Enable platform announcements"
          onChange={(e) => patch({ platformAnnouncements: e.target.checked })}
        />
      </label>

      <div className="wfx-settings-field">
        <span className="wfx-settings-label">Digest frequency</span>
        <div className="wfx-settings-options">
          {DIGEST_OPTIONS.map((mode) => (
            <button
              key={mode}
              type="button"
              className={`chip-pill ${prefs.digestMode === mode ? 'is-active' : ''}`}
              onClick={() => patch({ digestMode: mode })}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="wfx-settings-field">
        <span className="wfx-settings-label">Delivery channels</span>
        <div className="wfx-settings-toggles">
          {(configData?.channels ?? Object.keys(prefs.channels)).map((channel) => (
            <label key={channel} className="wfx-toggle-row">
              <span>{CHANNEL_LABELS[channel] ?? channel}</span>
              <input
                type="checkbox"
                checked={prefs.channels[channel] ?? false}
                aria-label={`Enable ${CHANNEL_LABELS[channel] ?? channel} notifications`}
                onChange={(e) =>
                  patch({ channels: { ...prefs.channels, [channel]: e.target.checked } })
                }
              />
            </label>
          ))}
        </div>
      </div>

      <div className="wfx-settings-field">
        <span className="wfx-settings-label">Quiet hours</span>
        <label className="wfx-toggle-row">
          <span>Do not disturb</span>
          <input
            type="checkbox"
            checked={prefs.quietHours.enabled}
            aria-label="Enable quiet hours"
            onChange={(e) =>
              patch({ quietHours: { ...prefs.quietHours, enabled: e.target.checked } })
            }
          />
        </label>
        {prefs.quietHours.enabled && (
          <div className="wfx-quiet-hours">
            <label>
              From
              <input
                type="time"
                value={prefs.quietHours.start}
                onChange={(e) =>
                  patch({ quietHours: { ...prefs.quietHours, start: e.target.value } })
                }
              />
            </label>
            <label>
              To
              <input
                type="time"
                value={prefs.quietHours.end}
                onChange={(e) =>
                  patch({ quietHours: { ...prefs.quietHours, end: e.target.value } })
                }
              />
            </label>
          </div>
        )}
      </div>

      <details className="wfx-settings-details">
        <summary>Notification categories</summary>
        <div className="wfx-settings-toggles">
          {(configData?.categories ?? []).map((cat) => (
            <label key={cat.id} className="wfx-toggle-row">
              <span>{cat.label}</span>
              <input
                type="checkbox"
                checked={prefs.categories[cat.id]?.enabled !== false}
                disabled={cat.id === 'account_security'}
                aria-label={`Enable ${cat.label} notifications`}
                onChange={(e) =>
                  patch({
                    categories: {
                      ...prefs.categories,
                      [cat.id]: { enabled: e.target.checked },
                    },
                  })
                }
              />
            </label>
          ))}
        </div>
      </details>
    </article>
  );
}

export function SettingsScreen() {
  const { settings, setSettings } = useAppearance();
  const { preview } = useUiSound();
  const { isAdmin, usesAdminConsoleOnly } = useAuth();

  return (
    <section className="adaptive-page">
      <header className="wfx-page-header">
        <div className="wfx-page-header-text">
          <SectionHeader
            kicker="Settings"
            title="Appearance & preferences"
            description="Customize theme, accent color, and notification preferences."
          />
        </div>
        <Illustration context="page.settings" size="md" className="wfx-page-header-illustration" />
      </header>

      <div className="wfx-settings-grid">
        <article className="glass-surface wfx-settings-card">
          <h2>Theme</h2>
          <div className="wfx-settings-options">
            {(['light', 'dark', 'system'] as const).map((theme) => (
              <button
                key={theme}
                type="button"
                className={`chip-pill ${settings.theme === theme ? 'is-active' : ''}`}
                onClick={() => setSettings({ theme })}
              >
                {theme.charAt(0).toUpperCase() + theme.slice(1)}
              </button>
            ))}
          </div>
        </article>

        <article className="glass-surface wfx-settings-card">
          <h2>Accent color</h2>
          <div className="wfx-settings-options">
            {(['orange', 'blue', 'green', 'purple', 'emerald'] as const).map((accent) => (
              <button
                key={accent}
                type="button"
                className={`chip-pill accent-${accent} ${settings.accent === accent ? 'is-active' : ''}`}
                onClick={() => setSettings({ accent })}
              >
                {accent.charAt(0).toUpperCase() + accent.slice(1)}
              </button>
            ))}
          </div>
        </article>

        <article className="glass-surface wfx-settings-card">
          <h2>Font size</h2>
          <div className="wfx-settings-options">
            {(['small', 'medium', 'large'] as const).map((fontSize) => (
              <button
                key={fontSize}
                type="button"
                className={`chip-pill ${settings.fontSize === fontSize ? 'is-active' : ''}`}
                onClick={() => setSettings({ fontSize })}
              >
                {fontSize.charAt(0).toUpperCase() + fontSize.slice(1)}
              </button>
            ))}
          </div>
        </article>

        <article className="glass-surface wfx-settings-card wfx-sound-settings" data-silent>
          <h2>Sound effects</h2>
          <p className="wfx-muted">Subtle feedback for uploads, downloads, and notifications. Off by default.</p>

          <label className="wfx-toggle-row">
            <span>Enable UI sounds</span>
            <input
              type="checkbox"
              checked={settings.soundEnabled}
              onChange={(e) => {
                const enabled = e.target.checked;
                setSettings({ soundEnabled: enabled });
                if (enabled) preview('click');
              }}
              aria-label="Enable UI sounds"
            />
          </label>

          <label className="wfx-sound-volume">
            <span>Volume · {settings.soundVolume}%</span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={settings.soundVolume}
              disabled={!settings.soundEnabled}
              onChange={(e) => setSettings({ soundVolume: Number(e.target.value) })}
              aria-label="Sound volume"
            />
          </label>

          <label className="wfx-toggle-row">
            <span>Respect reduced motion (mute sounds)</span>
            <input
              type="checkbox"
              checked={settings.respectReducedMotion}
              onChange={(e) => setSettings({ respectReducedMotion: e.target.checked })}
              aria-label="Respect reduced motion for sounds"
            />
          </label>

          <label className="wfx-toggle-row">
            <span>Mute when tab is hidden</span>
            <input
              type="checkbox"
              checked={settings.respectSystemSilent}
              onChange={(e) => setSettings({ respectSystemSilent: e.target.checked })}
              aria-label="Mute sounds when tab is hidden"
            />
          </label>

          <label className="wfx-toggle-row">
            <span>Reduce animations</span>
            <input
              type="checkbox"
              checked={!settings.animations}
              onChange={(e) => setSettings({ animations: !e.target.checked })}
              aria-label="Reduce animations"
            />
          </label>

          {settings.soundEnabled && (
            <div className="wfx-sound-preview">
              <button type="button" className="secondary-action compact" onClick={() => preview('success')}>
                Preview success
              </button>
              <button type="button" className="secondary-action compact" onClick={() => preview('notificationBell')}>
                Preview notification
              </button>
            </div>
          )}
        </article>

        <NotificationPreferencesPanel />

        {isAdmin && !usesAdminConsoleOnly && (
          <article className="glass-surface wfx-settings-card wfx-settings-card--wide">
            <h2>Administration</h2>
            <p className="wfx-muted">Open the WebFoxx admin command center to manage users, content, and platform operations.</p>
            <Link to="/admin" className="primary-action">Open Admin Dashboard</Link>
          </article>
        )}

        <article className="glass-surface wfx-settings-card wfx-settings-card--wide">
          <div className="wfx-settings-help">
            <div>
              <h2>Help & support</h2>
              <p className="wfx-muted">Get answers about results, vault uploads, saved exams, and notifications.</p>
            </div>
            <Illustration context="support.help" size="sm" className="wfx-settings-help-illustration" />
          </div>
          <label className="wfx-toggle-row">
            <span>Certificate expiry reminders</span>
            <input type="checkbox" defaultChecked aria-label="Enable certificate expiry reminders" />
          </label>
          <label className="wfx-toggle-row">
            <span>Calendar deadline alerts</span>
            <input type="checkbox" defaultChecked aria-label="Enable calendar deadline alerts" />
          </label>
        </article>
      </div>
    </section>
  );
}
